import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import PostAddIcon from '@mui/icons-material/PostAdd';
import { css } from '@emotion/react';

import { useIllustrationUrl } from '../../components/Illustrations/hooks/useIllustrationUrl.hook';
import { AddNotesIllustration } from '../../components/Illustrations/AddNotes/AddNotes';

import { Avatar } from '../Avatar';
import { SplitButton } from '../SplitButton';
import type { SplitButtonOption } from '../SplitButton';
import { GatedButton } from '../Access/GatedButton';
import { OPEN } from '../Access/utils/access-gate.util';
import type { AccessGate } from '../Access/types/access-gate.type';

import { DEFAULT_FOLDER_BUSINESS_ENTITY_PLACEHOLDER_HEIGHT } from './constants/card.constant';

interface Props {
  readonly onStartCreate: () => void;
  readonly newFolderBusinessEntityString: string;
  readonly createOptions?: readonly SplitButtonOption[];
  readonly onCreateOption?: (option: Readonly<SplitButtonOption>) => void;
  // Closed when the creation is refused: greyed out, the tooltip giving the reasons.
  readonly gate?: AccessGate;
}

export const NewFolderBusinessEntityCard = ({
  onStartCreate: handleStartCreate,
  newFolderBusinessEntityString,
  createOptions,
  onCreateOption: handleCreateOption,
  gate = OPEN,
}: Readonly<Props>) => {
  const imageUrl = useIllustrationUrl(AddNotesIllustration);

  return (
    <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        avatar={
          <Avatar
            icon={<PostAddIcon />}
            label={newFolderBusinessEntityString}
          />
        }
        css={css`
          .MuiCardHeader-action {
            display: contents;
          }
        `}
      />
      <CardMedia
        alt={newFolderBusinessEntityString}
        component="img"
        height={DEFAULT_FOLDER_BUSINESS_ENTITY_PLACEHOLDER_HEIGHT}
        image={imageUrl}
      />
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography color="text.secondary" variant="body1">
          {newFolderBusinessEntityString}
        </Typography>
      </CardContent>
      <CardActions>
        {createOptions?.length && handleCreateOption ? (
          <SplitButton
            ariaLabelButton="Choisir le type à créer"
            ariaLabelButtonGroup={newFolderBusinessEntityString}
            gate={gate}
            id="new-folder-business-entity-split-button"
            onClick={handleCreateOption}
            options={createOptions}
            size="medium"
          />
        ) : (
          <GatedButton
            gate={gate}
            onClick={handleStartCreate}
            size="medium"
            variant="outlined"
          >
            Formulaire
          </GatedButton>
        )}
      </CardActions>
    </Card>
  );
};
